/**
 * Markup renderers for Algolia results.
 *
 * These mirror the theme's own Liquid snippets so that the existing CSS and
 * custom elements keep working unchanged:
 *   - sections/predictive-search.liquid
 *   - snippets/predictive-search-tab-panel.liquid
 *   - snippets/predictive-search-tab-button.liquid
 *
 * If you change one of those snippets, change the matching renderer here.
 *
 * Exposes `theme.algoliaTemplates`.
 */
(() => {
  const config = (window.theme && window.theme.algolia) || {};

  /**
   * Escapes a value for safe insertion into HTML.
   * @param {*} value - Value to escape.
   * @returns {string}
   */
  function esc(value) {
    if (value === undefined || value === null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Formats an amount using the shop's money format.
   *
   * Algolia's Shopify app indexes prices in currency units (19.99), whereas
   * Shopify's own APIs use cents - hence the `unitsToCents` conversion.
   *
   * @param {number} amount - Amount in currency units.
   * @param {boolean} [withCurrency] - Force the currency code on/off.
   * @returns {string}
   */
  function formatMoney(amount, withCurrency) {
    if (amount === undefined || amount === null || Number.isNaN(Number(amount))) return '';

    const useCurrency = withCurrency === undefined ? config.money.showCurrencyCode : withCurrency;
    const format = useCurrency ? config.money.formatWithCurrency : config.money.format;
    const cents = Math.round(Number(amount) * 100);

    /**
     * @param {number} value - Amount in cents.
     * @param {number} precision - Decimal places.
     * @param {string} thousands - Thousands separator.
     * @param {string} decimal - Decimal separator.
     * @returns {string}
     */
    function withDelimiters(value, precision, thousands, decimal) {
      const fixed = (value / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    }

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  /**
   * Adds a width parameter to a Shopify CDN image URL.
   * @param {string} src - Image URL.
   * @param {number} width - Requested width in pixels.
   * @returns {string}
   */
  function imageUrl(src, width) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.href;
    } catch (error) {
      return src;
    }
  }

  /**
   * Builds an `srcset` for a Shopify CDN image.
   * @param {string} src - Image URL.
   * @param {Array<number>} widths - Widths to include.
   * @returns {string}
   */
  function imageSrcset(src, widths) {
    if (!src) return '';
    return widths.map((width) => `${imageUrl(src, width)} ${width}w`).join(', ');
  }

  /**
   * Picks the card image out of a record.
   *
   * Variant-mode records carry a single image URL; product-mode records carry
   * an `images` array, whose entries are objects in the Shopify app's output
   * but may be plain URLs in a hand-built index.
   *
   * @param {object} hit - Raw Algolia hit.
   * @returns {string}
   */
  function primaryImage(hit) {
    if (hit.product_image) return hit.product_image;
    if (hit.image) return hit.image;

    const first = Array.isArray(hit.images) ? hit.images[0] : null;
    if (!first) return '';
    return typeof first === 'string' ? first : first.url || '';
  }

  /**
   * Normalises an Algolia product record into the fields the renderers use.
   *
   * The Algolia Shopify app's field names differ slightly between
   * product-level and variant-level indexing, so each field falls back through
   * the known aliases.
   *
   * @param {object} hit - Raw Algolia hit.
   * @returns {object}
   */
  function normalizeProduct(hit) {
    // Cards show the product's price range, not the matched variant's price,
    // so the variants_* fields win over the record's own `price`.
    const price = hit.variants_min_price !== undefined ? hit.variants_min_price : hit.price;
    const maxPrice = hit.variants_max_price !== undefined ? hit.variants_max_price : price;
    const compareAtPrice = hit.variants_compare_at_price_min !== undefined
      ? hit.variants_compare_at_price_min
      : hit.compare_at_price;

    return {
      id: hit.id || hit.objectID,
      // Kept distinct from `id`: in variant mode the record's objectID is
      // "product_variant" while `id` is the product, and Insights events must
      // name the record, not the product.
      objectID: hit.objectID || '',
      title: hit.title || '',
      handle: hit.handle || '',
      url: `${config.routes.root.replace(/\/$/, '')}/products/${hit.handle}`,
      vendor: hit.vendor || '',
      image: primaryImage(hit),
      price,
      maxPrice,
      compareAtPrice,
      priceVaries: maxPrice !== undefined && price !== undefined && maxPrice > price,
      available: hit.inventory_available !== false,
      tags: Array.isArray(hit.tags) ? hit.tags : [],
      createdAt: hit.created_at || '',
      templateSuffix: hit.template_suffix || ''
    };
  }

  /**
   * Renders the card labels, mirroring snippets/product-label.liquid for the
   * labels the record has data for, in the same order and with the same
   * limit of two.
   * @param {object} product - Normalised product.
   * @returns {string}
   */
  function renderProductLabels(product) {
    const { labels, promotion } = config.card;
    if (!labels || labels.position === 'none') return '';

    const html = [];

    if (product.templateSuffix.includes('preorder')) html.push(labels.preorderHtml);

    // A promotion label replaces "New arrival", as in the Liquid.
    const promotionShowing = promotion.enabled && promotion.tag
      && product.tags.some((tag) => String(tag).toLowerCase() === promotion.tag);

    const { newLabel } = labels;
    if (newLabel.enabled && !promotionShowing) {
      const tagged = newLabel.tag
        && product.tags.some((tag) => String(tag).toLowerCase() === newLabel.tag);

      // Whole days since creation, compared the way the Liquid does.
      const created = Date.parse(product.createdAt);
      const recent = newLabel.byDays && !Number.isNaN(created)
        && Math.floor((Date.now() - created) / 86400000) < newLabel.dayLimit;

      if (tagged || recent) html.push(newLabel.html);
    }

    if (!html.length) return '';
    return `<div class="product-label-container absolute ${esc(labels.position)}">${html.slice(0, 2).join('')}</div>`;
  }

  /**
   * Renders the price block, mirroring snippets/price.liquid.
   * @param {object} product - Normalised product.
   * @param {string} [extraClass] - Additional CSS class.
   * @returns {string}
   */
  function renderPrice(product, extraClass) {
    const { promotion } = config.card;
    const onSale = product.compareAtPrice > product.price;

    const promotionActive = promotion.enabled
      && promotion.tag
      && product.tags.some((tag) => String(tag).toLowerCase() === promotion.tag);

    const classes = ['price'];
    if (onSale) classes.push('price--on-sale');
    if (promotionActive) classes.push('price--promotion');
    if (!product.available) classes.push('price--sold-out');
    if (extraClass) classes.push(extraClass);

    const fromLabel = product.priceVaries
      ? `<span class="price__from">${esc(config.strings.from)}</span>`
      : '';

    if (promotionActive) {
      const promoPrice = (product.price * (100 - promotion.discountPercent)) / 100;

      return `<div class="${classes.join(' ')}">
        <div class="price__default">
          <span class="price__current price__promo-current" style="font-weight: 700;">
            ${fromLabel}
            <span class="js-value">${formatMoney(promoPrice)}</span>
          </span>
          <span class="price__promo-was" style="text-decoration: line-through; opacity: 0.65; margin-left: 6px;">
            <span>${formatMoney(product.price, false)}</span>
          </span>
          ${onSale ? `<span class="price__promo-rrp" style="display: block; width: 100%; font-size: 0.85em; opacity: 0.65; margin-top: 2px;">
            <span>RRP </span><span>${formatMoney(product.compareAtPrice, false)}</span>
          </span>` : ''}
        </div>
      </div>`;
    }

    return `<div class="${classes.join(' ')}">
      <div class="price__default">
        <span class="price__current">
          <span class="visually-hidden js-label">${esc(onSale ? config.strings.salePrice : config.strings.regularPrice)}</span>
          ${fromLabel}
          <span class="js-value">${formatMoney(product.price)}</span>
        </span>
        <span class="price__was${config.money.superscriptDecimals ? ' price__was--supercents' : ''}">
          <span class="visually-hidden js-label">${onSale ? esc(config.strings.regularPrice) : ''}</span>
          <span class="js-value">${onSale ? formatMoney(product.compareAtPrice, false) : ''}</span>
        </span>
      </div>
    </div>`;
  }

  // --------------------------------------------------------------------------
  // Predictive search
  // --------------------------------------------------------------------------

  const TAB_LABELS = {
    products: () => config.strings.products,
    collections: () => config.strings.collections,
    pages: () => config.strings.pages,
    articles: () => config.strings.articles,
    articles_and_pages: () => `${config.strings.articles} &amp; ${config.strings.pages}`
  };

  /**
   * Renders a tab button, mirroring snippets/predictive-search-tab-button.liquid.
   *
   * Note the Liquid snippet is rendered outside a for loop, so `forloop.first`
   * is nil there and every tab is emitted unselected - tabs.js then activates
   * the first one. We reproduce that exactly.
   *
   * @param {string} type - Resource type.
   * @returns {string}
   */
  function renderTabButton(type) {
    return `<button type="button" class="tablist__tab font-bold" id="psearch-${type}-tab" role="tab" tabindex="-1" aria-controls="psearch-${type}" aria-selected="false">${TAB_LABELS[type]()}</button>`;
  }

  /**
   * Renders one predictive result row.
   * @param {object} item - Normalised item with url/title/image/subtitle, and
   *   objectID for products (absent for collections, pages and articles, which
   *   live in other indices and are not tracked).
   * @param {boolean} showMedia - Whether to render the media column.
   * @param {string} [blendClass] - Optional image-blend class.
   * @returns {string}
   */
  function renderPredictiveItem(item, showMedia, blendClass) {
    const instant = config.predictive.preloadLinks ? ' data-instant' : '';

    let media = '';
    if (showMedia) {
      media = item.image
        ? `<div class="predictive-result__media media relative${blendClass}">
             <img src="${esc(imageUrl(item.image, 112))}" alt="${esc(item.title)}" loading="eager" class="predictive-result__media">
           </div>`
        : '<div class="predictive-result__media media relative"></div>';
    }

    return `<li class="predictive-search__item" role="option"${item.objectID ? ` data-object-id="${esc(item.objectID)}"` : ''}>
      <a class="predictive-result flex items-start focus-inset js-search-link" href="${esc(item.url)}"${instant}>
        ${media}
        <div class="predictive-result__info flex-auto">
          <h3 class="predictive-result__title h6">${esc(item.title)}</h3>
          ${item.priceHtml || ''}
          ${item.subtitle ? `<div class="predictive-result__sub-title text-sm">${esc(item.subtitle)}</div>` : ''}
        </div>
      </a>
    </li>`;
  }

  /**
   * Renders a tab panel, mirroring snippets/predictive-search-tab-panel.liquid.
   * @param {string} type - Resource type.
   * @param {string} itemsMarkup - Rendered list items.
   * @returns {string}
   */
  function renderTabPanel(type, itemsMarkup) {
    return `<div class="predictive-search__tabpanel focus-inset" id="psearch-${type}" role="tabpanel" tabindex="0" aria-labelledby="psearch-${type}-tab" hidden>
      <ul class="predictive-search__list" role="list">
        ${itemsMarkup}
        <li class="predictive-search__item js-submit" role="option">
          <button class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link">${esc(config.strings.viewAll)} ${config.predictive.arrowIcon}</button>
        </li>
      </ul>
    </div>`;
  }

  /**
   * Collapses variant-level records down to one hit per product.
   *
   * Needed when the Algolia app indexes in "Variant" mode without an
   * `attributeForDistinct`, which otherwise repeats the same product once per
   * variant. Harmless in "Product" mode, where ids are already unique.
   *
   * @param {Array} hits - Raw Algolia hits.
   * @param {number} [limit] - Maximum number of products to keep.
   * @returns {Array}
   */
  function dedupeProducts(hits, limit) {
    const seen = new Set();
    const unique = [];

    hits.some((hit) => {
      const productId = hit.id || hit.objectID;
      if (seen.has(productId)) return false;

      seen.add(productId);
      unique.push(hit);
      return limit !== undefined && unique.length >= limit;
    });

    return unique;
  }

  const CARD_IMAGE_WIDTHS = [320, 460, 600, 700, 800, 900, 1200];

  /**
   * Renders a product card, mirroring snippets/product-card.liquid.
   *
   * Deliberately NOT reproduced, because the Algolia record carries no data
   * for them:
   *   - colour swatches (need Shopify option-value swatch metadata)
   *   - metafield and collection driven labels (see renderProductLabels)
   *   - hover/slideshow secondary images (only one image is indexed)
   *   - inventory messages (settings.card_show_inventory is off for this store)
   *
   * @param {object} hit - Raw Algolia hit.
   * @returns {string}
   */
  function renderProductCard(hit) {
    const product = normalizeProduct(hit);
    const card = config.card;
    const ratio = Number(card.imageRatio) || 1;

    const classes = ['card', 'card--product', 'h-full'];
    if (card.contain) classes.push('card--product-contained');
    if (!card.showDividers) classes.push('card--no-lines');
    if (card.showCompare) classes.push('card--product-compare');
    classes.push('relative', 'flex');

    const imageClass = card.imageFit === 'contain'
      ? 'img-fit img-fit--contain card__main-image'
      : `img-fit card__main-image ${card.imageAlign || ''}`.trim();

    const media = product.image
      ? `<img src="${esc(imageUrl(product.image, 460))}"
              srcset="${esc(imageSrcset(product.image, CARD_IMAGE_WIDTHS))}"
              sizes="(min-width: 769px) 25vw, 50vw"
              alt="${esc(product.title)}" loading="lazy" fetchpriority="low"
              class="${esc(imageClass)}">`
      : '<div class="media__placeholder img-fit"></div>';

    const compare = card.showCompare
      ? `<div class="card__compare no-js-hidden text-sm">
           <input type="checkbox" class="checkbox checkbox--compare js-compare-checkbox" id="compare-${esc(product.id)}"
                  data-product-id="${esc(product.id)}" data-product-url="${esc(product.url)}">
           <label for="compare-${esc(product.id)}">${esc(config.strings.compare)}</label>
         </div>`
      : '';

    const vendor = card.showVendor
      ? `<p class="card__vendor${card.showDividers ? ' mb-1' : ' mb-0'} text-sm text-theme-light">${esc(product.vendor)}</p>`
      : '';

    const priceTop = card.priceBottom ? '' : renderPrice(product, 'price--top');
    const priceBottom = card.priceBottom
      ? `<div class="flex grow items-end">${renderPrice(product, 'price--bottom')}</div>`
      : '';

    // Always the "choose options" variant of quick add: the Algolia record does
    // not tell us whether the product has only a default variant, and the
    // drawer resolves that itself from the product URL.
    const quickAddClasses = [
      'card__quick-add',
      card.atcMobile === 'text_button_bottom' ? 'mob:card__quick-add--below' : '',
      card.atcMobile === 'icon' ? 'mob:card__quick-add--icon' : '',
      card.atcDesktop === 'text_button_bottom' ? 'desktop:card__quick-add--below' : '',
      card.atcDesktop === 'icon' ? 'desktop:card__quick-add--icon' : ''
    ].filter(Boolean).join(' ');

    const quickAdd = card.quickAdd
      ? `<div class="${quickAddClasses}">
           <div class="no-js-hidden">
             <button type="button" class="btn btn--primary w-full js-quick-add" aria-haspopup="dialog"
                     data-product-url="${esc(product.url)}" data-product-default-variant="false">
               <span class="quick-add-btn-icon block pointer-events-none">
                 <span class="visually-hidden">${esc(config.strings.chooseOptions)}</span>
                 ${config.icons.addToCart || ''}
               </span>
               <span class="quick-add-btn-text block pointer-events-none">${esc(config.strings.chooseOptions)}</span>
             </button>
             <a href="${esc(product.url)}" class="btn btn--secondary quick-add-view-btn">${esc(config.strings.viewDetails)}</a>
           </div>
         </div>`
      : '';

    return `<product-card class="${classes.join(' ')}" data-product-id="${esc(product.id)}"${product.objectID ? ` data-object-id="${esc(product.objectID)}"` : ''}>
      ${compare}
      <div class="card__media relative">
        <a href="${esc(product.url)}" aria-label="${esc(product.title)}"
           class="media block relative${card.blendImages ? ' image-blend' : ''} js-prod-link"
           style="padding-top: ${(100 / ratio).toFixed(4)}%;" tabindex="-1">${media}</a>
        ${renderProductLabels(product)}
      </div>
      <div class="card__info-container flex flex-col flex-auto relative">
        <div class="card__info w-full">
          <div class="card__info-inner${card.priceBottom ? ' flex flex-col h-full' : ' inline-block'} w-full">
            ${vendor}
            <p class="card__title font-bold${card.showDividers ? ' mb-1' : ' mt-1 mb-0'}">
              <a href="${esc(product.url)}" class="card-link text-current js-prod-link">${esc(product.title)}</a>
            </p>
            ${priceTop}
            ${priceBottom}
          </div>
        </div>
        ${quickAdd}
      </div>
    </product-card>`;
  }

  /**
   * Renders one collapsible filter block, mirroring the `details-disclosure`
   * markup in snippets/facet-filters.liquid.
   *
   * @param {object} filter - { paramName, label, type, values }, where each
   *   value is { value, label, count, active }.
   * @param {object} opts - { showCounts, expand, maxOptions }.
   * @returns {string}
   */
  function renderFilterBlock(filter, opts) {
    const hasActive = filter.values.some((v) => v.active);

    const items = filter.values.map((value, index) => {
      const hidden = index >= opts.maxOptions && !value.active ? ' class="js-hidden"' : '';
      const id = `filter-${filter.paramName}-${index + 1}`;
      const count = opts.showCounts ? `<span class="filter__count">(${value.count})</span>` : '';

      return `<li${hidden}>
        <input type="checkbox" class="checkbox visually-hidden focus-label-before"
               id="${esc(id)}" name="${esc(filter.paramName)}" value="${esc(value.value)}"
               ${value.active ? 'checked' : ''}${value.count === 0 && !value.active ? ' disabled' : ''}>
        <label for="${esc(id)}" class="filter__label flex items-start w-full m-0">
          <span class="flex-auto">${esc(value.label)}</span>
          ${count}
        </label>
      </li>`;
    }).join('');

    const showMore = filter.values.length > opts.maxOptions
      ? `<button type="button" class="filter__more link text-current text-sm js-show-more">${esc(config.strings.filterShowMore)}</button>`
      : '';

    return `<details-disclosure>
      <details class="filter disclosure" id="filter-${esc(filter.paramName)}" data-type="${esc(filter.type)}"${opts.expand ? ' open' : ''}>
        <summary class="filter__toggle">
          <div class="flex justify-between items-center">
            <span class="flex-auto font-bold">${esc(filter.label)}</span>
            <span class="disclosure__toggle">${config.icons.disclosureToggle}</span>
          </div>
        </summary>
        <div class="disclosure__panel has-motion">
          <div class="disclosure__content">
            <div class="filter__content"><ul role="list">${items}</ul></div>
            <div class="disclosure__show-more flex relative items-center">
              ${showMore}
              <div class="filter__header">
                <a href="${esc(filter.clearUrl)}" class="link text-current text-sm js-clear-filter"${hasActive ? '' : ' hidden'}>${esc(config.strings.filterClear)}</a>
              </div>
            </div>
          </div>
        </div>
      </details>
    </details-disclosure>`;
  }

  /**
   * Renders the list of applied filter chips.
   * @param {Array} active - [{ label, removeUrl }].
   * @param {string} clearAllUrl - URL that clears every filter.
   * @returns {string}
   */
  function renderActiveFilters(active, clearAllUrl) {
    const heading = active.length
      ? config.strings.filterAppliedCount.replace('[count]', active.length)
      : config.strings.filterApplied;

    const chips = active.map((item) => `<a href="${esc(item.removeUrl)}" class="active-filter flex items-center text-current">
        <span>${esc(item.label)}</span>
      </a>`).join('');

    return `<details-disclosure class="facets__active-filters"${active.length ? '' : ' hidden'}>
      <details class="disclosure" id="active-filters" open>
        <summary>
          <div class="flex justify-between items-center">
            <span class="flex-auto font-bold active-filters-header">${esc(heading)}</span>
            <span class="disclosure__toggle">${config.icons.disclosureToggle}</span>
          </div>
        </summary>
        <div class="disclosure__panel has-motion">
          <div class="disclosure__content">
            <div class="active-filters flex flex-wrap">${chips}</div>
            <a href="${esc(clearAllUrl)}" class="link text-current text-sm js-clear-filter">${esc(config.strings.filterClearAll)}</a>
          </div>
        </div>
      </details>
    </details-disclosure>`;
  }

  /**
   * Renders the results grid, reusing the classes the Liquid grid carries so
   * the layout toggle and filters-open/closed classes keep working.
   * @param {Array} hits - Product hits.
   * @param {string} gridClasses - Class list captured from the Liquid grid.
   * @returns {string}
   */
  function renderResultsGrid(hits, gridClasses) {
    if (!hits.length) return `<p>${esc(config.strings.emptyFilters)}</p>`;

    const items = hits.map((hit) => `<li class="js-pagination-result">${renderProductCard(hit)}</li>`).join('');
    return `<ul class="${esc(gridClasses)}" role="list">${items}</ul>`;
  }

  const algoliaTemplates = {
    renderFilterBlock,
    renderActiveFilters,
    renderResultsGrid,
    formatMoney,
    imageUrl,
    imageSrcset,
    normalizeProduct,
    dedupeProducts,
    renderPrice,
    renderProductCard,

    /**
     * Renders the whole predictive search dropdown.
     * @param {object} groups - { products, collections, pages, articles } arrays of hits.
     * @param {string} term - The search term, for the no-results message.
     * @returns {string}
     */
    renderPredictiveResults(groups, term) {
      const root = config.routes.root.replace(/\/$/, '');
      const counts = {
        products: (groups.products || []).length,
        collections: (groups.collections || []).length,
        pages: (groups.pages || []).length,
        articles: (groups.articles || []).length
      };
      const total = counts.products + counts.collections + counts.pages + counts.articles;

      if (total === 0) {
        return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start">
          <div class="predictive-search__scroller overflow-auto">
            <div class="predictive-search__results flex flex-col">
              <div class="predictive-search__no-results">${esc(config.strings.predictiveNoResults).replace('[terms]', esc(term))}</div>
            </div>
          </div>
        </div>`;
      }

      const tabs = [];
      const panels = [];

      if (counts.products) {
        tabs.push(renderTabButton('products'));
        const blend = config.predictive.blendProductImages ? ' image-blend' : '';
        panels.push(renderTabPanel('products', groups.products.map((hit) => {
          const product = normalizeProduct(hit);
          return renderPredictiveItem({
            url: product.url,
            objectID: product.objectID,
            title: product.title,
            image: product.image,
            subtitle: config.predictive.showVendor ? product.vendor : '',
            priceHtml: config.predictive.showPrice ? renderPrice(product) : ''
          }, true, blend);
        }).join('')));
      }

      if (counts.collections) {
        tabs.push(renderTabButton('collections'));
        const blend = config.predictive.blendCollectionImages ? ' image-blend' : '';
        panels.push(renderTabPanel('collections', groups.collections.map((hit) => renderPredictiveItem({
          url: `${root}/collections/${hit.handle}`,
          title: hit.title,
          image: hit.image,
          subtitle: (hit.meta && hit.meta.theme && hit.meta.theme.label) || ''
        }, true, blend)).join('')));
      }

      // Articles and pages share a tab when both are present, as in the Liquid.
      if (counts.pages && counts.articles) {
        tabs.push(renderTabButton('articles_and_pages'));
        const items = groups.articles.map((hit) => renderPredictiveItem({
          url: `${root}/blogs/${hit.blog_handle || hit.blog}/${hit.handle}`,
          title: hit.title,
          image: hit.image,
          subtitle: hit.author || ''
        }, true)).concat(groups.pages.map((hit) => renderPredictiveItem({
          url: `${root}/pages/${hit.handle}`,
          title: hit.title,
          subtitle: config.strings.page
        }, false)));
        panels.push(renderTabPanel('articles_and_pages', items.join('')));
      } else if (counts.pages) {
        tabs.push(renderTabButton('pages'));
        panels.push(renderTabPanel('pages', groups.pages.map((hit) => renderPredictiveItem({
          url: `${root}/pages/${hit.handle}`,
          title: hit.title
        }, false)).join('')));
      } else if (counts.articles) {
        tabs.push(renderTabButton('articles'));
        panels.push(renderTabPanel('articles', groups.articles.map((hit) => renderPredictiveItem({
          url: `${root}/blogs/${hit.blog_handle || hit.blog}/${hit.handle}`,
          title: hit.title,
          image: hit.image,
          subtitle: hit.author || ''
        }, true)).join('')));
      }

      return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start">
        <div class="predictive-search__scroller overflow-auto">
          <div class="predictive-search__results flex flex-col">
            <tabbed-content class="grow">
              <div class="tablist relative overflow-hidden bg-theme-bg mb-2">
                <div class="tablist__scroller flex" role="tablist">${tabs.join('')}</div>
              </div>
              ${panels.join('')}
            </tabbed-content>
          </div>
        </div>
      </div>`;
    }
  };

  window.theme = window.theme || {};
  window.theme.algoliaTemplates = algoliaTemplates;
})();
